import { CopyItemsFromRoomDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { getItemsQueryKey } from './get-items';

export type { CopyItemsFromRoomDto };

/**
 * MT1-1393 redesign: the POST endpoint now enqueues an asynchronous job and
 * returns the Job entity. The frontend treats success as "the copy started";
 * the items themselves appear via the items-query invalidation triggered by
 * the DDB stream once the job worker completes. Hieu's review on PR #234 has
 * the full rationale (sync path was timing out on moderate-size rooms).
 *
 * Adaptive polling against `GET /jobs/{jobId}` for a per-step progress UI is
 * a follow-up — the immediate concern is the timeout, not progress UX.
 */
export interface CopyRoomJobResponse {
  id: string;
  type: 'CopyRoomItems' | 'CopyRoomToFloor';
  status: 'pending' | 'running' | 'completed' | 'failed';
  createdAt: number;
  updatedAt: number;
}

const copyItemsFromRoomRequest = async (
  orgId: string,
  projectId: string,
  buildingId: string,
  roomId: string,
  data: CopyItemsFromRoomDto,
): Promise<CopyRoomJobResponse> => {
  const { data: result } = await api.post<unknown, ApiResponse<CopyRoomJobResponse>>(
    `/orgs/${orgId}/projects/${projectId}/buildings/${buildingId}/rooms/${roomId}/copy-items-from-room`,
    data,
  );
  return result;
};

interface UseCopyItemsFromRoomOptions {
  mutationConfig?: MutationConfig<
    (args: {
      buildingId: string;
      roomId: string;
      dto: CopyItemsFromRoomDto;
    }) => Promise<CopyRoomJobResponse>
  >;
}

export const useCopyItemsFromRoom = ({ mutationConfig }: UseCopyItemsFromRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');
  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      // The job is enqueued — items will land via the stream-driven items
      // query invalidation once the worker writes them, so kick off the
      // invalidation here. The user-facing message reflects the async shape.
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      // Skip the default success toast when the caller supplies its own
      // onSuccess — otherwise the dialog and the hook each show one and the
      // user sees a duplicate notification.
      if (!onSuccess) {
        notifications.success('items.copyFromRoom.queued');
      } else {
        onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (!onError) {
        notifications.error('items.copyFromRoom.error');
      } else {
        onError(...args);
      }
    },
    ...restConfig,
    mutationFn: ({
      buildingId,
      roomId,
      dto,
    }: {
      buildingId: string;
      roomId: string;
      dto: CopyItemsFromRoomDto;
    }) => copyItemsFromRoomRequest(orgId, projectId, buildingId, roomId, dto),
  });
};
